/**
 * apiConfig.ts
 *
 * Force-uses the real API client. Provides named wrappers that delegate to the
 * real API implementation and a default export object for convenience.
 *
 * The file intentionally avoids any mock client usage so the application always
 * calls the real backend.
 */

import * as realApi from "./api";

/**
 * Force using the real API only.
 */
const USE_REAL_API = true;

console.log(
  "🔧 API Config:",
  USE_REAL_API ? "Using REAL API 🌐" : "Using MOCK API 🎭",
);
if (typeof window !== "undefined") {
  console.log("🔍 Current URL search params:", window.location.search);
}
console.log(
  "📍 API Base URL:",
  process.env.REACT_APP_API_URL || "https://api.connect-esim.com/api",
);

/**
 * Return the real API module object.
 */
const getApiModule = () => realApi as any;

/**
 * Generic caller that resolves the actual function on the selected module.
 * It tries:
 *   1) module[fnName]
 *   2) module.default && module.default[fnName]
 *
 * Then calls using apply to preserve potential `this` context.
 */
async function callApi(fnName: string, ...args: any[]) {
  const moduleObj: any = getApiModule();

  let fn: any = moduleObj && moduleObj[fnName];
  if (typeof fn !== "function" && moduleObj && moduleObj.default) {
    fn = moduleObj.default[fnName];
  }

  if (typeof fn !== "function") {
    throw new Error(
      `API function "${fnName}" is not available on the real API client.`,
    );
  }

  return await fn.apply(moduleObj, args);
}

/**
 * Named wrappers. These keep signatures flexible (rest args) so callers don't need to change.
 */
export const fetchUsers = async (...args: any[]) =>
  callApi("fetchUsers", ...args);
export const fetchUserFilterOptions = async (...args: any[]) =>
  callApi("fetchUserFilterOptions", ...args);
export const loginUser = async (...args: any[]) =>
  callApi("loginUser", ...args);
export const createUser = async (...args: any[]) =>
  callApi("createUser", ...args);
export const updateUser = async (...args: any[]) =>
  callApi("updateUser", ...args);
export const deleteUser = async (...args: any[]) =>
  callApi("deleteUser", ...args);
export const updateUserBalance = async (...args: any[]) =>
  callApi("updateUserBalance", ...args);

export const fetchSimCards = async (...args: any[]) =>
  callApi("fetchSimCards", ...args);
export const createSimCard = async (...args: any[]) =>
  callApi("createSimCard", ...args);
export const updateSimCard = async (...args: any[]) =>
  callApi("updateSimCard", ...args);
export const deleteSimCard = async (...args: any[]) =>
  callApi("deleteSimCard", ...args);

export const fetchImsiMaps = async (...args: any[]) =>
  callApi("fetchImsiMaps", ...args);
export const createImsiMap = async (...args: any[]) =>
  callApi("createImsiMap", ...args);
export const updateImsiMap = async (...args: any[]) =>
  callApi("updateImsiMap", ...args);
export const deleteImsiMap = async (...args: any[]) =>
  callApi("deleteImsiMap", ...args);

export const fetchOfferNumbers = async (...args: any[]) =>
  callApi("fetchOfferNumbers", ...args);
export const createOfferNumber = async (...args: any[]) =>
  callApi("createOfferNumber", ...args);
export const updateOfferNumber = async (...args: any[]) =>
  callApi("updateOfferNumber", ...args);
export const deleteOfferNumber = async (...args: any[]) =>
  callApi("deleteOfferNumber", ...args);

export const assignFreeEsim = async (...args: any[]) =>
  callApi("assignFreeEsim", ...args);
export const fetchAvailableEsims = async (...args: any[]) =>
  callApi("fetchAvailableEsims", ...args);

export const fetchNumbers = async (...args: any[]) =>
  callApi("fetchNumbers", ...args);
export const createNumber = async (...args: any[]) =>
  callApi("createNumber", ...args);
export const updateNumber = async (...args: any[]) =>
  callApi("updateNumber", ...args);
export const deleteNumber = async (...args: any[]) =>
  callApi("deleteNumber", ...args);

export const fetchDashboardStats = async (...args: any[]) =>
  callApi("fetchDashboardStats", ...args);

export const fetchEsimOrders = async (...args: any[]) =>
  callApi("fetchEsimOrders", ...args);
export const createEsimOrder = async (...args: any[]) =>
  callApi("createEsimOrder", ...args);
export const updateEsimOrder = async (...args: any[]) =>
  callApi("updateEsimOrder", ...args);
export const deleteEsimOrder = async (...args: any[]) =>
  callApi("deleteEsimOrder", ...args);

/**
 * Default export: use the real API client's default export if present,
 * otherwise expose the wrapper functions in a convenience object.
 */
const defaultClient =
  (realApi as any).default ??
  ({
    fetchUsers,
    loginUser,
    createUser,
    updateUser,
    deleteUser,
    updateUserBalance,

    fetchSimCards,
    createSimCard,
    updateSimCard,
    deleteSimCard,

    fetchImsiMaps,
    createImsiMap,
    updateImsiMap,
    deleteImsiMap,

    fetchOfferNumbers,
    createOfferNumber,
    updateOfferNumber,
    deleteOfferNumber,

    fetchNumbers,
    createNumber,
    updateNumber,
    deleteNumber,

    fetchDashboardStats,

    fetchEsimOrders,
    createEsimOrder,
    updateEsimOrder,
    deleteEsimOrder,

    assignFreeEsim,
    fetchAvailableEsims,
  } as any);

export default defaultClient;
